import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Formik, Form, Field, ErrorMessage, useFormikContext } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
    FaUsers, FaUserPlus, FaChevronLeft, FaChevronRight,
    FaMapMarkerAlt, FaSeedling, FaCoins, FaTimes, FaExclamationTriangle,
    FaCheck, FaBan, FaPencilAlt,
} from "react-icons/fa";
import { getFPOMembers, addFPOMember, updateFPOMember, getFPOMemberLocations } from "../../services/fpoMember";
import { getFPOStates, getFPODistricts } from "../../services/location";
import { getFPOData } from "../../helpers/local_storage_helpers";
import PageHeader from "../../components/header/PageHeader";
import SearchableSelect from "../../components/inputFields/SearchableSelect";

// ── Edit member form (approved rows) — everything but mobile number ──
const EditMemberSchema = Yup.object().shape({
    full_name:    Yup.string().required("Full name is required"),
    line1:        Yup.string(),
    village:      Yup.string(),
    teshil:       Yup.string(),
    city:         Yup.string(),
    district:     Yup.string(),
    state:        Yup.string(),
    zipcode:      Yup.string(),
    share_held:   Yup.number().min(0, "Must be ≥ 0").nullable().transform(v => (isNaN(v) ? null : v)),
    share_amount: Yup.number().min(0, "Must be ≥ 0").nullable().transform(v => (isNaN(v) ? null : v)),
    member_since: Yup.date().nullable(),
});

// ── Add Farmer form ──────────────────────────────────────────────
const AddFarmerSchema = Yup.object().shape({
    full_name:     Yup.string().required("Full name is required"),
    mobile_number: Yup.string().matches(/^[0-9]{10}$/, "Enter a valid 10-digit mobile number").required("Mobile number is required"),
    line1:         Yup.string(),
    village:       Yup.string(),
    teshil:        Yup.string(),
    city:          Yup.string(),
    district:      Yup.string(),
    state:         Yup.string(),
    zipcode:       Yup.string(),
    share_held:    Yup.number().min(0, "Must be ≥ 0").nullable().transform(v => (isNaN(v) ? null : v)),
    share_amount:  Yup.number().min(0, "Must be ≥ 0").nullable().transform(v => (isNaN(v) ? null : v)),
    member_since:  Yup.date().nullable(),
});

// Defaults new farmers to the FPO's own state/district — most members farm
// locally, so this saves an admin picking the same two dropdowns every time.
const getAddFarmerInitial = () => {
    const fpo = getFPOData() || {};
    return {
        full_name: "", mobile_number: "",
        line1: "", village: "", teshil: "", city: "",
        district: fpo.district || "", state: fpo.state || "", zipcode: fpo.zip_code || "",
        share_held: "", share_amount: "", member_since: "",
    };
};

// ── Modal shell ──────────────────────────────────────────────────
const Modal = ({ title, icon: Icon, onClose, children }) => (
    <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[1100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
    >
        <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
                <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon className="text-primary text-sm" />
                    </div>
                    <h3 className="font-bold text-gray-800 text-sm">{title}</h3>
                </div>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg transition-colors">
                    <FaTimes />
                </button>
            </div>
            <div className="p-5">{children}</div>
        </motion.div>
    </motion.div>
);

const FieldWrap = ({ label, name, required, children }) => (
    <div>
        <label className="block text-xs font-semibold text-gray-500 mb-1">
            {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        {children}
        <ErrorMessage name={name} component="p" className="text-red-500 text-xs mt-1" />
    </div>
);

const inputCls = "w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary";

// ── State / District cascading dropdowns ──────────────────────────
const LocationFields = () => {
    const { values, setFieldValue } = useFormikContext();
    const [states, setStates]               = useState([]);
    const [districts, setDistricts]         = useState([]);
    const [statesLoading, setStatesLoading]     = useState(false);
    const [districtsLoading, setDistrictsLoading] = useState(false);

    useEffect(() => {
        setStatesLoading(true);
        getFPOStates()
            .then((res) => {
                const data = res?.data;
                if (data?.status === 200 || data?.status === "200") setStates(data.data || []);
            })
            .catch(() => {})
            .finally(() => setStatesLoading(false));
    }, []);

    useEffect(() => {
        if (!values.state) { setDistricts([]); return; }
        setDistrictsLoading(true);
        getFPODistricts(values.state)
            .then((res) => {
                const data = res?.data;
                if (data?.status === 200 || data?.status === "200") setDistricts(data.data || []);
            })
            .catch(() => setDistricts([]))
            .finally(() => setDistrictsLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.state]);

    return (
        <>
            <FieldWrap name="state" label="State">
                <SearchableSelect
                    value={values.state}
                    onChange={(v) => {
                        setFieldValue("state", v);
                        setFieldValue("district", "");
                    }}
                    disabled={statesLoading}
                    placeholder={statesLoading ? "Loading…" : "— Select state —"}
                    searchPlaceholder="Search states…"
                    options={states.map((s) => ({ value: s.name, label: s.name }))}
                    className={inputCls}
                />
            </FieldWrap>
            <FieldWrap name="district" label="District">
                <SearchableSelect
                    value={values.district}
                    onChange={(v) => setFieldValue("district", v)}
                    disabled={!values.state || districtsLoading}
                    placeholder={!values.state ? "Select a state first" : districtsLoading ? "Loading…" : "— Select district —"}
                    searchPlaceholder="Search districts…"
                    options={districts.map((d) => ({ value: d.name, label: d.name }))}
                    className={`${inputCls} disabled:opacity-60 disabled:bg-gray-50`}
                />
            </FieldWrap>
        </>
    );
};

// ── Status Badge ──────────────────────────────────────────────────
// Rejected/pending surface as-is; an approved + active member reads as "Member".
const statusPill = (m) => {
    if (m.status === "rejected") return { label: m.status_display || "Rejected", cls: "bg-red-50 text-red-600" };
    if (m.status === "pending")  return { label: m.status_display || "Pending",  cls: "bg-amber-50 text-amber-700" };
    if (!m.is_active)            return { label: "Inactive", cls: "bg-gray-100 text-gray-500" };
    return { label: "Member", cls: "bg-primary/10 text-primary" };
};

const StatusBadge = ({ member }) => {
    const { label, cls } = statusPill(member);
    return (
        <span className={`inline-flex items-center text-xs font-medium px-3 py-1 rounded-full ${cls}`}>
            {label}
        </span>
    );
};

// ── Farmer avatar (initials) ───────────────────────────────────────
const initials = (name) => (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join("");

const Avatar = ({ name }) => (
    <span className="w-7 h-7 rounded-full bg-primary/15 text-primary text-[11px] font-bold flex items-center justify-center flex-shrink-0">
        {initials(name)}
    </span>
);

// ── Row actions — vary by status ─────────────────────────────────
const RowActions = ({ member, busy, onApprove, onReject, onToggleActive, onEdit }) => {
    if (member.status === "pending") {
        return (
            <div className="flex items-center justify-end gap-1.5">
                <button
                    type="button"
                    disabled={busy}
                    onClick={() => onApprove(member)}
                    title="Approve"
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors disabled:opacity-50"
                >
                    <FaCheck size={11} />
                </button>
                <button
                    type="button"
                    disabled={busy}
                    onClick={() => onReject(member)}
                    title="Reject"
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors disabled:opacity-50"
                >
                    <FaBan size={11} />
                </button>
            </div>
        );
    }
    if (member.status === "rejected") {
        return (
            <div className="flex items-center justify-end">
                <button
                    type="button"
                    disabled={busy}
                    onClick={() => onApprove(member)}
                    title="Approve"
                    className="text-xs font-semibold text-primary hover:underline disabled:opacity-50"
                >
                    Approve
                </button>
            </div>
        );
    }
    // approved
    return (
        <div className="flex items-center justify-end gap-2">
            <button
                type="button"
                disabled={busy}
                onClick={() => onEdit(member)}
                title="Edit"
                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors disabled:opacity-50"
            >
                <FaPencilAlt size={11} />
            </button>
            <button
                type="button"
                disabled={busy}
                onClick={() => onToggleActive(member)}
                title={member.is_active ? "Deactivate" : "Activate"}
                className={`relative w-9 h-5 rounded-full transition-colors duration-200 disabled:opacity-50 ${
                    member.is_active ? "bg-primary" : "bg-gray-300"
                }`}
            >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                    member.is_active ? "translate-x-4" : "translate-x-0"
                }`} />
            </button>
        </div>
    );
};

// ── Stat Card ─────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, accent }) => (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${accent || "bg-primary/10"}`}>
            <Icon className="text-primary text-base" />
        </div>
        <div>
            <p className="text-[11px] text-gray-400 font-medium">{label}</p>
            <p className="text-base font-bold text-gray-800">{value}</p>
        </div>
    </div>
);

// ── Status tabs — approved, pending, rejected (in that order) ──────
const STATUS_TABS = [
    { value: "approved", label: "Approved", countKey: "approved_count" },
    { value: "pending",  label: "Pending",  countKey: "pending_count" },
    { value: "rejected", label: "Rejected", countKey: "rejected_count" },
];

const PAGE_SIZE = 10;

const FPOMembers = () => {
    const [members, setMembers] = useState([]);
    const [stats, setStats]     = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState(null);

    // Filters
    const [search, setSearch]             = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("approved");
    const [activeFilter, setActiveFilter] = useState("all");
    const [villageFilter, setVillageFilter] = useState("all");
    const [tehsilFilter, setTehsilFilter]   = useState("all");
    const [locations, setLocations]         = useState({ villages: [], tehsils: [] });

    // Pagination
    const [page, setPage]   = useState(1);
    const [count, setCount] = useState(0);
    const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

    // Add farmer
    const [showAddModal, setShowAddModal] = useState(false);
    const [conflict, setConflict]         = useState(null);

    // Row actions
    const [actingId, setActingId]     = useState(null);
    const [rejectTarget, setRejectTarget] = useState(null);
    const [rejectReason, setRejectReason] = useState("");
    const [rejecting, setRejecting]       = useState(false);
    const [editTarget, setEditTarget]     = useState(null);

    const loadMembers = async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { page, page_size: PAGE_SIZE };
            if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
            params.status = statusFilter;
            if (activeFilter !== "all") params.is_active = activeFilter === "active";
            if (villageFilter !== "all") params.village = villageFilter;
            if (tehsilFilter !== "all") params.tehsil = tehsilFilter;

            const res  = await getFPOMembers(params);
            const data = res?.data;
            if (data?.status === 200 || data?.status === "200") {
                setMembers(data.results || []);
                setStats(data.stats || null);
                setCount(data.count ?? 0);
            } else {
                setError(data?.message || "Failed to load members.");
            }
        } catch {
            setError("Unable to connect. Please check the server.");
        } finally {
            setLoading(false);
        }
    };

    // Debounce search input, resetting to page 1 once it settles
    useEffect(() => {
        const t = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 400);
        return () => clearTimeout(t);
    }, [search]);

    // Fetch whenever page or any filter changes
    useEffect(() => {
        loadMembers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, debouncedSearch, statusFilter, activeFilter, villageFilter, tehsilFilter]);

    // Load village/tehsil options for the filter dropdowns once
    useEffect(() => {
        getFPOMemberLocations()
            .then((res) => {
                const data = res?.data;
                if (data?.status === 200 || data?.status === "200") {
                    setLocations({
                        villages: data.data?.villages || [],
                        tehsils:  data.data?.tehsils || [],
                    });
                }
            })
            .catch(() => {});
    }, []);

    const fmt = (val) => {
        if (val == null || val === "") return "—";
        const n = parseFloat(val);
        return isNaN(n) ? val : (n % 1 === 0 ? n.toString() : n.toFixed(2).replace(/\.?0+$/, ""));
    };

    const fmtLocation = (m) => [m.village, m.tehsil].filter(Boolean).join(" / ") || "—";

    const fmtCurrency = (val) => {
        const n = parseFloat(val);
        return isNaN(n) ? "0" : n.toLocaleString("en-IN", { maximumFractionDigits: 0 });
    };

    // ── Add farmer submit ────────────────────────────────────────
    const handleAddFarmer = async (values, { resetForm }) => {
        setConflict(null);
        try {
            const payload = {
                ...values,
                share_held:   values.share_held !== "" ? parseInt(values.share_held, 10) : 0,
                share_amount: values.share_amount !== "" ? parseFloat(values.share_amount) : 0,
                member_since: values.member_since || null,
            };
            const res  = await addFPOMember(payload);
            const data = res?.data;
            const ok   = data?.status === 200 || data?.status === 201 || res?.status === 200 || res?.status === 201;

            if (ok) {
                toast.success("Farmer registered — pending approval. ✅");
                resetForm();
                setShowAddModal(false);
                setStatusFilter("pending");
                setActiveFilter("all");
                setPage(1);
            } else {
                const errs = data?.errors;
                toast.error((errs && Object.values(errs).flat().join(", ")) || data?.message || "Could not register farmer.");
            }
        } catch (e) {
            const data = e?.response?.data;
            if (e?.response?.status === 409 && data?.warning) {
                setConflict({
                    message: data.message || "This person is already registered with another FPO.",
                    fpoName: data.fpo_name || data.existing_fpo || null,
                    status:  data.status_display || data.status || null,
                });
                return;
            }
            const errs = data?.errors;
            toast.error((errs && Object.values(errs).flat().join(", ")) || data?.message || "Something went wrong.");
        }
    };

    // ── Row action helpers ───────────────────────────────────────
    const patchMember = async (id, payload, successMsg) => {
        setActingId(id);
        try {
            const res  = await updateFPOMember(id, payload);
            const data = res?.data;
            const ok   = data?.status === 200 || data?.status === "200" || res?.status === 200;
            if (ok) {
                toast.success(successMsg);
                await loadMembers();
                return true;
            }
            const errs = data?.errors;
            toast.error((errs && Object.values(errs).flat().join(", ")) || data?.message || "Update failed.");
            return false;
        } catch (e) {
            const data = e?.response?.data;
            const errs = data?.errors;
            toast.error((errs && Object.values(errs).flat().join(", ")) || data?.message || "Something went wrong.");
            return false;
        } finally {
            setActingId(null);
        }
    };

    const handleApprove = (m) => patchMember(m.id, { status: "approved" }, "Farmer approved. ✅");

    const handleToggleActive = (m) => patchMember(
        m.id,
        { is_active: !m.is_active },
        `Farmer ${!m.is_active ? "activated" : "deactivated"}.`,
    );

    const handleRejectSubmit = async () => {
        if (!rejectTarget) return;
        setRejecting(true);
        const ok = await patchMember(
            rejectTarget.id,
            { status: "rejected", rejection_reason: rejectReason.trim() },
            "Farmer rejected.",
        );
        setRejecting(false);
        if (ok) {
            setRejectTarget(null);
            setRejectReason("");
        }
    };

    const handleEditSubmit = async (values) => {
        if (!editTarget) return;
        const payload = {
            full_name:    values.full_name,
            line1:        values.line1,
            village:      values.village,
            teshil:       values.teshil,
            city:         values.city,
            district:     values.district,
            state:        values.state,
            zipcode:      values.zipcode,
            share_held:   values.share_held !== "" ? parseInt(values.share_held, 10) : 0,
            share_amount: values.share_amount !== "" ? parseFloat(values.share_amount) : 0,
            member_since: values.member_since || null,
            is_active:    values.is_active,
        };

        const ok = await patchMember(editTarget.id, payload, "Member updated.");
        if (ok) setEditTarget(null);
    };

    return (
        <div className="w-full pb-12">

            {/* ── Page Header ── */}
            <PageHeader
                title="Farmers & Members"
                search={search}
                onSearchChange={setSearch}
                searchPlaceholder="Search name, mobile, member code…"
                actionLabel="Add farmer"
                actionIcon={FaUserPlus}
                onAction={() => { setConflict(null); setShowAddModal(true); }}
            />
            <p className="text-[11px] text-gray-500 mb-3 px-1">{count} total member{count === 1 ? "" : "s"}</p>

            {/* ── Stats ── */}
            {stats && (
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5"
                >
                    <StatCard icon={FaUsers}    label="Total"    value={stats.total_members} />
                    <StatCard icon={FaUsers}    label="Active"   value={stats.active_members} />
                    <StatCard icon={FaUsers}    label="Inactive" value={stats.inactive_members} />
                    <StatCard icon={FaUsers}    label="Pending"  value={stats.pending_count} />
                    <StatCard icon={FaSeedling} label="Approved" value={stats.approved_count} />
                    <StatCard icon={FaCoins}    label="Total Shares" value={`₹${fmt(stats.total_share_amount)}`} />
                </motion.div>
            )}

            {/* ── Status tabs ── */}
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center justify-between flex-wrap gap-3 mb-4"
            >
                <div className="inline-flex items-center gap-1 bg-white border border-gray-100 shadow-sm rounded-xl p-1">
                    {STATUS_TABS.map(tab => {
                        const active = statusFilter === tab.value;
                        const count  = stats?.[tab.countKey];
                        return (
                            <button
                                key={tab.value}
                                type="button"
                                onClick={() => { setStatusFilter(tab.value); setPage(1); }}
                                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                                    active ? "bg-primary text-white shadow-sm" : "text-gray-500 hover:bg-gray-50"
                                }`}
                            >
                                {tab.label}
                                {count != null && (
                                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                                        active ? "bg-white/20 text-white" : "bg-gray-100 text-gray-500"
                                    }`}>
                                        {count}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                    {/* Village Filter */}
                    <div className="w-40">
                        <SearchableSelect
                            value={villageFilter}
                            onChange={v => { setVillageFilter(v); setPage(1); }}
                            options={[{ value: "all", label: "All Villages" }, ...locations.villages.map(v => ({ value: v, label: v }))]}
                            className="pl-3 pr-8 py-2.5 text-sm border border-gray-200 rounded-xl bg-white"
                        />
                    </div>

                    {/* Tehsil Filter */}
                    <div className="w-40">
                        <SearchableSelect
                            value={tehsilFilter}
                            onChange={v => { setTehsilFilter(v); setPage(1); }}
                            options={[{ value: "all", label: "All Tehsils" }, ...locations.tehsils.map(t => ({ value: t, label: t }))]}
                            className="pl-3 pr-8 py-2.5 text-sm border border-gray-200 rounded-xl bg-white"
                        />
                    </div>

                    {/* Active Filter */}
                    <div className="w-36">
                        <SearchableSelect
                            value={activeFilter}
                            onChange={v => { setActiveFilter(v); setPage(1); }}
                            options={[
                                { value: "all", label: "All" },
                                { value: "active", label: "Active" },
                                { value: "inactive", label: "Inactive" },
                            ]}
                            className="pl-3 pr-8 py-2.5 text-sm border border-gray-200 rounded-xl bg-white"
                        />
                    </div>
                </div>
            </motion.div>

            {/* ── Content ── */}
            {loading ? (
                <div className="flex flex-col items-center justify-center h-64 gap-3">
                    <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                    <p className="text-gray-500 text-sm">Loading members…</p>
                </div>
            ) : error ? (
                <div className="flex flex-col items-center justify-center h-64 gap-3">
                    <p className="text-red-500 font-medium">{error}</p>
                    <button onClick={loadMembers} className="px-4 py-2 bg-primary text-white rounded-lg text-sm">Retry</button>
                </div>
            ) : members.length === 0 ? (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex flex-col items-center justify-center h-56 gap-3 text-center bg-white rounded-2xl border border-gray-100"
                >
                    <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center">
                        <FaUsers className="text-gray-300 text-3xl" />
                    </div>
                    <p className="font-semibold text-gray-500 text-sm">No members found</p>
                </motion.div>
            ) : (
                <>
                    {/* Table (desktop) */}
                    <div className="hidden md:block bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-100 text-left text-[11px] text-gray-500 uppercase tracking-wide">
                                    {statusFilter === "approved" && (
                                        <th className="px-5 py-3 font-semibold">Member Code</th>
                                    )}
                                    <th className="px-5 py-3 font-semibold">Farmer</th>
                                    <th className="px-5 py-3 font-semibold">Village / Tehsil</th>
                                    <th className="px-5 py-3 font-semibold">Shares · ₹</th>
                                    <th className="px-5 py-3 font-semibold">Farms</th>
                                    <th className="px-5 py-3 font-semibold">Status</th>
                                    <th className="px-5 py-3 font-semibold text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <AnimatePresence>
                                    {members.map((m) => (
                                        <motion.tr
                                            key={m.id}
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            className="border-b border-gray-50 last:border-0 odd:bg-primary/[0.03] hover:bg-primary/[0.06] transition-colors"
                                        >
                                            {statusFilter === "approved" && (
                                                <td className="px-5 py-3.5 font-semibold text-gray-700">{m.member_code}</td>
                                            )}
                                            <td className="px-5 py-3.5">
                                                <div className="flex items-center gap-2.5">
                                                    <Avatar name={m.name} />
                                                    <div>
                                                        <p className="font-semibold text-gray-800">{m.name || "—"}</p>
                                                        {m.mobile_number && (
                                                            <p className="text-[11px] text-gray-400">{m.mobile_number}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-5 py-3.5 text-gray-600">{fmtLocation(m)}</td>
                                            <td className="px-5 py-3.5 text-gray-700">
                                                {m.share_held} · ₹{fmtCurrency(m.share_amount)}
                                            </td>
                                            <td className="px-5 py-3.5 text-gray-700">
                                                {m.no_of_farms ?? 0} farm{m.no_of_farms === 1 ? "" : "s"}
                                                <span className="text-gray-400"> · {fmt(m.farm_area_acres)} ac</span>
                                            </td>
                                            <td className="px-5 py-3.5">
                                                <StatusBadge member={m} />
                                            </td>
                                            <td className="px-5 py-3.5">
                                                <RowActions
                                                    member={m}
                                                    busy={actingId === m.id}
                                                    onApprove={handleApprove}
                                                    onReject={setRejectTarget}
                                                    onToggleActive={handleToggleActive}
                                                    onEdit={setEditTarget}
                                                />
                                            </td>
                                        </motion.tr>
                                    ))}
                                </AnimatePresence>
                            </tbody>
                        </table>
                    </div>

                    {/* Cards (mobile) */}
                    <div className="md:hidden flex flex-col gap-3">
                        <AnimatePresence>
                            {members.map((m) => (
                                <motion.div
                                    key={m.id}
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0 }}
                                    className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4"
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="flex items-center gap-2.5 min-w-0">
                                            <Avatar name={m.name} />
                                            <div className="min-w-0">
                                                <p className="font-semibold text-gray-800 text-sm truncate">{m.name || "—"}</p>
                                                <p className="text-[11px] text-gray-400">
                                                    {[statusFilter === "approved" ? m.member_code : null, m.mobile_number]
                                                        .filter(Boolean)
                                                        .join(" · ")}
                                                </p>
                                            </div>
                                        </div>
                                        <StatusBadge member={m} />
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 mt-3 text-xs text-gray-600">
                                        <div className="flex items-center gap-1">
                                            <FaMapMarkerAlt className="text-gray-300" size={10} />
                                            {fmtLocation(m)}
                                        </div>
                                        <div>{m.share_held} · ₹{fmtCurrency(m.share_amount)}</div>
                                        <div>{m.no_of_farms ?? 0} farm{m.no_of_farms === 1 ? "" : "s"}</div>
                                        <div>{fmt(m.farm_area_acres)} ac</div>
                                    </div>
                                    <div className="mt-3 pt-3 border-t border-gray-50">
                                        <RowActions
                                            member={m}
                                            busy={actingId === m.id}
                                            onApprove={handleApprove}
                                            onReject={setRejectTarget}
                                            onToggleActive={handleToggleActive}
                                            onEdit={setEditTarget}
                                        />
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>

                    {/* ── Pagination ── */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between mt-5">
                            <p className="text-xs text-gray-500">
                                Page {page} of {totalPages}
                            </p>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 disabled:opacity-40 hover:bg-gray-50"
                                >
                                    <FaChevronLeft size={11} />
                                </button>
                                <button
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 disabled:opacity-40 hover:bg-gray-50"
                                >
                                    <FaChevronRight size={11} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* ── Add Farmer Modal ── */}
            <AnimatePresence>
                {showAddModal && (
                    <Modal
                        title="Add Farmer"
                        icon={FaUserPlus}
                        onClose={() => { setShowAddModal(false); setConflict(null); }}
                    >
                        <Formik
                            initialValues={getAddFarmerInitial()}
                            validationSchema={AddFarmerSchema}
                            onSubmit={handleAddFarmer}
                        >
                            {({ isSubmitting }) => (
                                <Form className="flex flex-col gap-4">
                                    {conflict && (
                                        <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-100 text-amber-800 text-xs rounded-xl px-3.5 py-3">
                                            <FaExclamationTriangle className="mt-0.5 flex-shrink-0" />
                                            <div>
                                                <p className="font-semibold">{conflict.message}</p>
                                                {(conflict.fpoName || conflict.status) && (
                                                    <p className="mt-0.5 text-amber-700">
                                                        {conflict.fpoName && <>FPO: <span className="font-medium">{conflict.fpoName}</span></>}
                                                        {conflict.fpoName && conflict.status && " · "}
                                                        {conflict.status && <>Status: <span className="font-medium">{conflict.status}</span></>}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide -mb-1">Farmer Details</p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <FieldWrap name="full_name" label="Full Name" required>
                                            <Field name="full_name" placeholder="e.g. Ram Singh" className={inputCls} />
                                        </FieldWrap>
                                        <FieldWrap name="mobile_number" label="Mobile Number" required>
                                            <Field name="mobile_number" placeholder="10-digit mobile number" maxLength={10} className={inputCls} />
                                        </FieldWrap>
                                    </div>

                                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide -mb-1">Address</p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <FieldWrap name="line1" label="Address Line">
                                            <Field name="line1" placeholder="House / street" className={inputCls} />
                                        </FieldWrap>
                                        <FieldWrap name="village" label="Village">
                                            <Field name="village" className={inputCls} />
                                        </FieldWrap>
                                        <FieldWrap name="teshil" label="Tehsil">
                                            <Field name="teshil" className={inputCls} />
                                        </FieldWrap>
                                        <FieldWrap name="city" label="City">
                                            <Field name="city" className={inputCls} />
                                        </FieldWrap>
                                        <LocationFields />
                                        <FieldWrap name="zipcode" label="Zip Code">
                                            <Field name="zipcode" className={inputCls} />
                                        </FieldWrap>
                                    </div>

                                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide -mb-1">Membership</p>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                        <FieldWrap name="share_held" label="Shares Held">
                                            <Field name="share_held" type="number" min="0" step="1" className={inputCls} />
                                        </FieldWrap>
                                        <FieldWrap name="share_amount" label="Share Amount (₹)">
                                            <Field name="share_amount" type="number" min="0" step="0.01" className={inputCls} />
                                        </FieldWrap>
                                        <FieldWrap name="member_since" label="Member Since">
                                            <Field name="member_since" type="date" className={inputCls} />
                                        </FieldWrap>
                                    </div>

                                    <div className="flex gap-3 pt-2 border-t border-gray-100">
                                        <button
                                            type="button"
                                            onClick={() => { setShowAddModal(false); setConflict(null); }}
                                            className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-semibold rounded-xl text-sm hover:bg-gray-50 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="flex-1 py-2.5 bg-primary hover:bg-green-600 text-white font-semibold rounded-xl text-sm shadow-lg shadow-primary/20 transition-colors disabled:opacity-60"
                                        >
                                            {isSubmitting ? "Saving…" : "Add Farmer"}
                                        </button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </Modal>
                )}
            </AnimatePresence>

            {/* ── Reject Modal ── */}
            <AnimatePresence>
                {rejectTarget && (
                    <Modal
                        title={`Reject – ${rejectTarget.name}`}
                        icon={FaBan}
                        onClose={() => { setRejectTarget(null); setRejectReason(""); }}
                    >
                        <div className="flex flex-col gap-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 mb-1">
                                    Rejection Reason <span className="text-gray-400 font-normal">(optional)</span>
                                </label>
                                <textarea
                                    value={rejectReason}
                                    onChange={(e) => setRejectReason(e.target.value)}
                                    rows={3}
                                    placeholder="e.g. Duplicate KYC, unreachable, ineligible…"
                                    className={inputCls}
                                />
                            </div>
                            <div className="flex gap-3 pt-2 border-t border-gray-100">
                                <button
                                    type="button"
                                    onClick={() => { setRejectTarget(null); setRejectReason(""); }}
                                    className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-semibold rounded-xl text-sm hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    disabled={rejecting}
                                    onClick={handleRejectSubmit}
                                    className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl text-sm transition-colors disabled:opacity-60"
                                >
                                    {rejecting ? "Rejecting…" : "Reject Farmer"}
                                </button>
                            </div>
                        </div>
                    </Modal>
                )}
            </AnimatePresence>

            {/* ── Edit Member Modal ── */}
            <AnimatePresence>
                {editTarget && (
                    <Modal
                        title={`Edit – ${editTarget.name}`}
                        icon={FaPencilAlt}
                        onClose={() => setEditTarget(null)}
                    >
                        <Formik
                            initialValues={{
                                full_name:    editTarget.name ?? "",
                                line1:        editTarget.line1 ?? "",
                                village:      editTarget.village ?? "",
                                teshil:       editTarget.tehsil ?? "",
                                city:         editTarget.city ?? "",
                                district:     editTarget.district ?? "",
                                state:        editTarget.state ?? "",
                                zipcode:      editTarget.zip_code ?? "",
                                share_held:   editTarget.share_held ?? "",
                                share_amount: editTarget.share_amount ?? "",
                                member_since: editTarget.member_since ?? "",
                                is_active:    editTarget.is_active ?? true,
                            }}
                            validationSchema={EditMemberSchema}
                            onSubmit={handleEditSubmit}
                        >
                            {({ isSubmitting, values, setFieldValue }) => (
                                <Form className="flex flex-col gap-4">
                                    <p className="text-[11px] text-gray-400 bg-gray-50 rounded-lg px-3 py-2">
                                        Mobile number <span className="font-semibold text-gray-500">{editTarget.mobile_number || "—"}</span> can't be changed here.
                                    </p>

                                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide -mb-1">Farmer Details</p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <FieldWrap name="full_name" label="Full Name" required>
                                            <Field name="full_name" className={inputCls} />
                                        </FieldWrap>
                                        <div className="flex items-center gap-3 pt-5">
                                            <button
                                                type="button"
                                                onClick={() => setFieldValue("is_active", !values.is_active)}
                                                className={`relative w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none ${
                                                    values.is_active ? "bg-primary" : "bg-gray-300"
                                                }`}
                                            >
                                                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                                                    values.is_active ? "translate-x-5" : "translate-x-0"
                                                }`} />
                                            </button>
                                            <span className="text-sm font-semibold text-gray-700">
                                                {values.is_active ? "Active" : "Inactive"}
                                            </span>
                                        </div>
                                    </div>

                                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide -mb-1">Address</p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <FieldWrap name="line1" label="Address Line">
                                            <Field name="line1" placeholder="House / street" className={inputCls} />
                                        </FieldWrap>
                                        <FieldWrap name="village" label="Village">
                                            <Field name="village" className={inputCls} />
                                        </FieldWrap>
                                        <FieldWrap name="teshil" label="Tehsil">
                                            <Field name="teshil" className={inputCls} />
                                        </FieldWrap>
                                        <FieldWrap name="city" label="City">
                                            <Field name="city" className={inputCls} />
                                        </FieldWrap>
                                        <LocationFields />
                                        <FieldWrap name="zipcode" label="Zip Code">
                                            <Field name="zipcode" className={inputCls} />
                                        </FieldWrap>
                                    </div>

                                    <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide -mb-1">Membership</p>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                        <FieldWrap name="share_held" label="Shares Held">
                                            <Field name="share_held" type="number" min="0" step="1" className={inputCls} />
                                        </FieldWrap>
                                        <FieldWrap name="share_amount" label="Share Amount (₹)">
                                            <Field name="share_amount" type="number" min="0" step="0.01" className={inputCls} />
                                        </FieldWrap>
                                        <FieldWrap name="member_since" label="Member Since">
                                            <Field name="member_since" type="date" className={inputCls} />
                                        </FieldWrap>
                                    </div>

                                    <div className="flex gap-3 pt-2 border-t border-gray-100">
                                        <button
                                            type="button"
                                            onClick={() => setEditTarget(null)}
                                            className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-semibold rounded-xl text-sm hover:bg-gray-50 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="flex-1 py-2.5 bg-primary hover:bg-green-600 text-white font-semibold rounded-xl text-sm shadow-lg shadow-primary/20 transition-colors disabled:opacity-60"
                                        >
                                            {isSubmitting ? "Saving…" : "Save Changes"}
                                        </button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </Modal>
                )}
            </AnimatePresence>
        </div>
    );
};

export default FPOMembers;
